import type {
  AttachLegalObligationEvidenceResponse,
  LegalObligationEvidencesResponse,
  LegalOrgObligationsResponse,
  MaterializeLegalObligationInstanceResponse,
  UploadLegalObligationExemptionBody,
} from '@safetyhub/shared';
import { api, type ApiResponse } from './api';

/**
 * 법정 서류 **조직 층**(②-g3 G4 — 기관 «자기»의 의무 · 기관 종사자 직무교육) API 클라이언트. 서버 `legal-obligation-org.controller.ts`
 * (`/legal-documents/organization/…`)의 짝이고 응답 형상의 정본은 shared `features/legal-documents.ts`의 `LegalOrgObligationsResponse`다.
 *
 * ⛔ 모든 호출이 `?companyId=<현재 작업 조직 id>`를 싣는다(서버 `OrganizationGuard` — 형제 `lib/legalDocuments.ts`와 같은 규약). 대상 조직은
 * 그 query 자신이다(path에 조직이 없다 — 남의 조직 경로가 원리적으로 없다).
 *
 * 현장 층 모듈(`lib/legalDocuments.ts`·`lib/legalObligationEvidence.ts`)과 갈라 둔 이유: 그 모듈들은 화면 spec이 `vi.mock`하는 대상이라 새
 * export가 그 spec들을 `No "X" export`로 죽인다.
 */

export const legalOrgObligationsQueryKeys = {
  obligations: (companyId: string) => ['legal-documents', companyId, 'organization', 'obligations'] as const,
  evidences: (companyId: string, instanceId: string) => ['legal-documents', companyId, 'organization', 'instances', instanceId, 'evidences'] as const,
};

function unwrap<T>(body: ApiResponse<T>): T {
  if (!body.success || body.data === null) throw body;
  return body.data;
}

const BASE = '/legal-documents/organization';

export async function getOrgObligations(companyId: string): Promise<LegalOrgObligationsResponse> {
  const { data } = await api.get<ApiResponse<LegalOrgObligationsResponse>>(`${BASE}/obligations`, { params: { companyId } });
  return unwrap(data);
}

/** 그 기관 종사자의 지금 기간 인스턴스(멱등) — 증빙을 붙일 자리 */
export async function materializeOrgInstance(
  companyId: string,
  obligationId: string,
  body: { subjectKey: string; periodKey?: string },
): Promise<MaterializeLegalObligationInstanceResponse> {
  const { data } = await api.post<ApiResponse<MaterializeLegalObligationInstanceResponse>>(
    `${BASE}/obligations/${encodeURIComponent(obligationId)}/instances`,
    body,
    { params: { companyId } },
  );
  return unwrap(data);
}

export async function listOrgEvidences(companyId: string, instanceId: string): Promise<LegalObligationEvidencesResponse> {
  const { data } = await api.get<ApiResponse<LegalObligationEvidencesResponse>>(
    `${BASE}/instances/${encodeURIComponent(instanceId)}/evidences`,
    { params: { companyId } },
  );
  return unwrap(data);
}

/** 이수증 등 외부 파일 업로드 + 연결 — multipart(텍스트 칸은 문자열 · 서버 DTO가 ISO 날짜를 검증한다) */
export async function uploadOrgEvidenceFile(
  companyId: string,
  instanceId: string,
  input: { file: File; fulfilledAt: string; note?: string },
): Promise<AttachLegalObligationEvidenceResponse> {
  const formData = new FormData();
  formData.append('file', input.file);
  formData.append('fulfilledAt', input.fulfilledAt);
  if (input.note) formData.append('note', input.note);
  const { data } = await api.post<ApiResponse<AttachLegalObligationEvidenceResponse>>(
    `${BASE}/instances/${encodeURIComponent(instanceId)}/evidences/file`,
    formData,
    { params: { companyId }, headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return unwrap(data);
}

/**
 * 사람 단위 교육 면제(2026-11-05 결정 3 — 기관 종사자 행에 제30조 면제) — 현장 입구 `uploadLegalObligationExemption`과 같은 multipart 칸.
 * 권한 `obligation.exempt` · 규칙은 shared `UploadLegalObligationExemptionBody`
 */
export async function uploadOrgExemption(
  companyId: string,
  instanceId: string,
  input: { file: File; basis: UploadLegalObligationExemptionBody['basis']; fulfilledAt: string; note?: string; confirmed?: boolean },
): Promise<AttachLegalObligationEvidenceResponse> {
  const formData = new FormData();
  formData.append('file', input.file);
  formData.append('basis', input.basis);
  formData.append('fulfilledAt', input.fulfilledAt);
  if (input.note) formData.append('note', input.note);
  if (input.confirmed) formData.append('confirmed', 'true');
  const { data } = await api.post<ApiResponse<AttachLegalObligationEvidenceResponse>>(
    `${BASE}/instances/${encodeURIComponent(instanceId)}/evidences/exemption`,
    formData,
    { params: { companyId }, headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return unwrap(data);
}

export async function downloadOrgEvidenceFile(companyId: string, evidenceId: string): Promise<Blob> {
  const response = await api.get(`${BASE}/evidences/${encodeURIComponent(evidenceId)}/file`, { params: { companyId }, responseType: 'blob' });
  return response.data as Blob;
}
